import React, { useState, useEffect } from "react";
import NavBar from "../../layout/navbar/navbar";
import { Container, Row, Col, Card, Alert, Accordion } from "react-bootstrap";
import { BsLightning, BsGraphUp, BsTools, BsPeople } from "react-icons/bs";
import { IoMdFlower } from "react-icons/io";
import {
  FaTruck,
  FaCar,
  FaThermometerHalf,
  FaLaptopCode,
  FaBriefcaseMedical,
  FaSchool,
  FaBuilding,
  FaWineGlassAlt,
} from "react-icons/fa";
import { GiTrenchSpade } from "react-icons/gi";
import { RiScissorsLine } from "react-icons/ri";
import { useSupabaseApp } from "../../SupabaseApp";
import "../dashboard/dashboard copy.css";
import "./capital-financing.css";
import Image1 from "../../../assets/capital-financing/capital-financing-1.jpg";
import Image2 from "../../../assets/capital-financing/capital-financing-2.jpg";
import Image3 from "../../../assets/capital-financing/capital-financing-3.jpg";
import ApplicationButton from "./application-button";
import { FaChevronDown } from "react-icons/fa";

/**
 * The `CapitalFinancing` component in React displays information about business financing options,
 * statistics, how the financing process works, and the benefits of the financing, along with relevant
 * images and icons.
 * @returns The `CapitalFinancing` component is being returned. It contains JSX elements that make up
 * the layout of a capital financing landing page. The component includes a navigation bar, various
 * sections with information about business financing statistics, how the financing process works, and
 * how businesses can benefit from the financing options. The component also includes images and icons
 * to enhance the visual presentation of the content.
 */

const CapitalFinancing = () => {
  const { getUserTier } = useSupabaseApp();

  const [tier, setTier] = useState();

  useEffect(() => {
    const checkTier = async () => {
      const tierValue = await getUserTier();
      setTier(tierValue);
    };

    checkTier();
  }, []);

  const statistics = [
    {
      number: "5K+",
      title: "Businesses funded",
      subTitle:
        "LightBox proudly supported over 5,000 businesses, helping them secure the necessary funds to expand and reach new heights.",
    },
    {
      number: "$300M+",
      title: "Funds disbursed",
      subTitle:
        "LightBox successfully disbursed over USD $300 million, empowering many businesses to grow, innovate, and achieve their financial goals.",
    },
    {
      number: "4 days",
      title: "Average to funding",
      subTitle:
        "LightBox's efficient process helps businesses secure the funding they need in an average of just 4 days, allowing them to focus on their next steps.",
    },
  ];

  const howItWorks = {
    title: "How it works",
    subTitle:
      "Securing the right funding for your business is simple. Just follow our 4-step process to get the flexible financing you need, fast.",
    objects: [
      {
        number: 1,
        title: "Apply online",
        subTitle:
          "Complete a quick and easy online application with your business details and desired loan amount",
      },
      {
        number: 2,
        title: "Review and approval",
        subTitle:
          "Our team reviews your application and provides a decision, often within minutes",
      },
      {
        number: 3,
        title: "Choose your terms",
        subTitle:
          "Select the loan terms that best fit your business needs, with flexible repayment options",
      },
      {
        number: 4,
        title: "Receive your funds",
        subTitle:
          "Once approved, the funds are transferred directly to your account, so you can start using them right away",
      },
    ],
  };

  const howYouBenefit = {
    title: "How you benefit",
    subTitle:
      "Our loans are designed to help professionals across a wide range of industries.",
    objects: [
      {
        icon: <BsLightning />,
        title: "Electrical services",
      },
      {
        icon: <IoMdFlower />,
        title: "Lawn care services",
      },
      {
        icon: <FaTruck />,
        title: "Transportation services",
      },
      {
        icon: <FaCar />,
        title: "Auto repair shops",
      },
      {
        icon: <BsGraphUp />,
        title: "Consulting services",
      },
      {
        icon: <GiTrenchSpade />,
        title: "Construction contractors",
      },
      {
        icon: <RiScissorsLine />,
        title: "Grooming services",
      },
      {
        icon: <FaThermometerHalf />,
        title: "HVAC technicians",
      },
      {
        icon: <FaLaptopCode />,
        title: "IT firms",
      },
      {
        icon: <FaBriefcaseMedical />,
        title: "Medical professionals",
      },
      {
        icon: <BsTools />,
        title: "Plumbing services",
      },
      {
        icon: <FaSchool />,
        title: "Preschools",
      },
      {
        icon: <FaBuilding />,
        title: "Real estate brokerages",
      },
      {
        icon: <FaWineGlassAlt />,
        title: "Restaurant owners",
      },
      {
        icon: <BsPeople />,
        title: "Staffing firms",
      },
    ],
  };

  const faqs = [
    {
      question: "How do I know which product is suitable for me?",
      answer:
        "Apply now and schedule a call with one of our experienced loan experts who will talk through your business needs and goals to help you make that determination.",
    },
    {
      question: "Is this available for anyone around the world?",
      answer:
        "No, this is currently available for our annual Premium plan customers with companies based in the United States and Canada.",
    },
    {
      question: "How many lending options are available through LightBox?",
      answer:
        "We have more than 85 active lenders in our embedded finance partner's marketplace across many different products.",
    },
    {
      question: "Is there a minimum or maximum loan amount?",
      answer:
        "This depends on which product you end up with. There is no lending minimum in our embedded finance partner's marketplace, with options up to US$10 million.",
    },
    {
      question:
        "What is the minimum amount of revenue to qualify for the loans?",
      answer:
        "While you may optimally receive good offers at minimum US$10,000 in monthly recurring revenue, we encourage you to try applying once you have constantly earned more than 6 months of minimum US$5,000 in monthly revenue, to understand your lending offers and options.",
    },
    {
      question: "What are the repayment terms?",
      answer:
        "This depends on which product you eventually select. We have options as little as 3 months, and as long as up to 10 years.",
    },
    {
      question:
        "Is there a financing option for me if my credit score is poor?",
      answer:
        "Absolutely. We have financing options available that do not require a credit check or pull credit reports/scores.",
    },
    {
      question: "What types of loans can LightBox offer?",
      answer:
        "Bank term loans, SBA loans, lines of credit, receivables financing, equipment financing, invoice factoring, consumer financing, credit restoration, and more.",
    },
    {
      question: "What can I use the funds for?",
      answer:
        "The options are endless. Typical uses of funds are for working capital, equipment purchases, business expansion, payroll, marketing, hiring, and so on.",
    },
    {
      question: "Does LightBox charge fees?",
      answer:
        "No, never. The only fees you will see are from the lending partner directly.",
    },
    {
      question: "How long will the financing take?",
      answer:
        "This depends on the product you choose. We do have options that can be funded in less than 24 hours.",
    },
    {
      question: "How do the interest rates work?",
      answer:
        "Interest rates represent the amount borrowers pay lenders to borrow money, that are usually expressed as a percentage of the loan's principal per year. Interest rates also vary by product. Therefore, be sure to ask your loan expert for more information.",
    },
    {
      question: "Do I need to provide collateral?",
      answer:
        "The majority of our lenders do not require any collateral for financing.",
    },
    {
      question: "Can you fund startup businesses?",
      answer: "We do have funding options for startups through the SBA.",
    },
    {
      question: "How can I be sure my private business information is safe?",
      answer:
        "We ensure our lenders only receive data necessary to make a decision to offer credit. Our lenders have access to 'Applicant Data', as defined in the business funding application where all business owners consent to, prior to applying for credit. LightBox is SOC 2 Type 2 compliant and has extensive security measures in place to ensure the protection of your personal and business information.",
    },
  ];

  return (
    <>
      <NavBar />

      <div id="cfLandingPage">
        <Container fluid className="cfContainer">
          <Row xs={1} sm={1} lg={2}>
            <Col>
              <p className="sectionHeader">
                Capital financing for roiquant customers
              </p>
              <p className="sectionText">
                Fast and flexible business financing for growth stage startups
                and medium-sized businesses
              </p>
              <p className="sectionSubText">
                Helping your business thrive with quick, transparent, and easy
                financing solutions, such as merchant cash advance, lines of
                credit, receivables financing, invoice factoring, equipment
                financing, term loans, and SBA loans.
              </p>

              <Row>
                <Col>
                  <div>
                    <ApplicationButton title="Apply for financing" />
                    {tier >= 3 ? (
                      <a className="cfLink" href="/borrower-platform">
                        Borrower login
                      </a>
                    ) : (
                      ""
                    )}
                  </div>
                </Col>
              </Row>

              <Alert className="cfAlert">
                <p>LightBox is roiquant's embedded finance partner.</p>
              </Alert>
            </Col>

            <Col>
              <div className="cfImageDiv">
                <img src={Image3} />
              </div>
            </Col>
          </Row>

          <Row xs={1} sm={3}>
            {statistics.map((object, index) => (
              <Col key={index}>
                <p className="sectionText">{object.number}</p>
                <p className="cfCardHeader">{object.title}</p>
                <p className="cfCardText">{object.subTitle}</p>
              </Col>
            ))}
          </Row>

          <Row xs={1} sm={1} lg={2}>
            <Col>
              {/* <Card className="cfMainCard">
                <Card.Body className="cfCardBody"> */}
              <p className="sectionText">{howItWorks.title}</p>
              <p className="sectionH3Text">{howItWorks.subTitle}</p>
            </Col>

            <Col></Col>
          </Row>

          <Row xs={1} sm={1} lg={2} style={{ paddingTop: 0 }}>
            <Col>
              {howItWorks.objects.map((object, index) => (
                <Row xs={2} sm={2} key={index}>
                  <Col xs={3} sm={3}>
                    <div className="cfBoxNumber">
                      <p className="cfNumber">{object.number}</p>
                    </div>
                  </Col>
                  <Col xs={9} sm={9} className="cfCol">
                    <p className="cfCardHeader">{object.title}</p>
                    <p className="cfCardText">{object.subTitle}</p>
                  </Col>
                </Row>
              ))}
              {/* </Card.Body>
              </Card> */}
            </Col>

            <Col>
              <div className="cfImageDiv">
                <img src={Image1} />
              </div>
            </Col>
          </Row>

          <Row xs={1} sm={1} lg={2}>
            <Col>
              {/* <Card className="cfMainCard">
                <Card.Body> */}
              <p className="sectionText">{howYouBenefit.title}</p>
              <p className="sectionH3Text">{howYouBenefit.subTitle}</p>
            </Col>

            <Col></Col>
          </Row>

          <Row xs={1} sm={1} lg={2} style={{ paddingTop: 0 }}>
            <Col>
              <Card className="cfMainCard">
                <Card.Body className="cfCardBody">
                  <Row xs={1} sm={1} md={3} lg={1} xl={3}>
                    {howYouBenefit.objects.map((object, index) => (
                      <Col key={index} className="cfCol">
                        <Card className="cfSubCard">
                          <Card.Body>
                            <p className="cfCardTextIcon">{object.title}</p>
                            <div className="cfBoxIcon">
                              <p className="cfIcon">{object.icon}</p>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </Card.Body>
              </Card>
              {/* </Card.Body>
              </Card> */}
            </Col>

            <Col>
              <div className="cfImageDiv">
                <img src={Image2} />
              </div>
            </Col>
          </Row>

          <Row xs={1} sm={1} lg={1}>
            <Col style={{ textAlign: "center" }}>
              <p className="sectionText">Frequently Asked Questions</p>
            </Col>

            <Col xs={8} sm={8} md={6} lg={6} style={{ margin: "0 auto" }}>
              <Accordion className="cfAccordion">
                {faqs.map((object, index) => (
                  <Accordion.Item eventKey={index} key={index}>
                    <Accordion.Header className="detailcardheader">
                      <div className="faqTitle"> {object.question}</div>

                      <div className="faqArrow">
                        <FaChevronDown />
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>{object.answer}</Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default CapitalFinancing;
